import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  InstantSearch,
  Configure,
  connectRefinementList,
  connectHits
} from 'react-instantsearch-dom';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { trackProductClick } from '../../utils/tracking';

import ProductCard from '../product-card/algolia';
import Carousel from '../../atoms/carousel';

const Slide = styled(ProductCard)`
  margin-top: ${(props) => props.theme.modularScale['3xlarge']};
  outline: none;
  padding: 0 20px;

  a {
    text-decoration: none;
  }
`;

const Product = styled(ProductCard)`
  width: 50%;
  text-align: center;
  margin-top: 4rem;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    width: 25%;
  }

  &.circle {
    figure {
      border-radius: 50%;
    }
  }
`;

const NoSlider = styled.span`
  display: flex;
  flex-wrap: wrap;
  ${({ numChildren }) => (numChildren < 4 ? css`
    justify-content: center;
  ` : '')}
`;

const VirtualRefinementList = connectRefinementList(() => null);

const setStyle = (styleName, index) => {
  if (styleName === 'both') {
    return index % 2 ? 'circle' : 'square';
  }
  return styleName;
};

const createRefinementAttributes = (search) => {
  /* on home page, search is an empty object
   * Update on above: this is due to no starting '/'
   * TODO is there a possibility that both of these RefinementLists
   * would need to be active on a single Algolia query?
  */
  if (search) {
    if (search.hierarchicalMenu && search.hierarchicalMenu['categories.lvl0'] !== '') {
      const list = Object.keys(search.hierarchicalMenu);
      const defaultRefinement = Object.values(search.hierarchicalMenu);
      const splitHeirarchies = search.hierarchicalMenu['categories.lvl0'].split('>');
      if (splitHeirarchies.length > 1) {
        // TODO this is a hack, which occurs in the algolia utils helpers file
        list.splice(0, 1, `categories.lvl${splitHeirarchies.length - 1}`);
      }
      return list.map((listName, index) => (
        <VirtualRefinementList
          attribute={listName}
          key={listName}
          defaultRefinement={defaultRefinement[index]}
        />
      ));
    }
    if (search.refinementList) {
      const list = Object.keys(search.refinementList);
      return list.map((listName) => (
        <VirtualRefinementList
          attribute={listName}
          key={listName}
          defaultRefinement={search.refinementList[listName]}
        />
      ));
    }
  }
  return null;
};

const TaxonProductWrapper = (props) => {
  const { hits } = props;

  const [carouselRef, setCarouselRef] = useState(null);

  const onCarouselRef = useCallback((node) => {
    if (node !== null) {
      setCarouselRef(node);
    }
  }, []);

  const carouselSettings = {
    groupCells: true,
    prevNextButtons: true,
    pageDots: false
  };

  const length = carouselRef?.selectedCells?.length ?? 1;

  if (props.isSlider && (hits.length >= length)) {
    return (
      <Carousel
        flickityRef={onCarouselRef}
        type="taxon-products"
        options={carouselSettings}
        reloadOnUpdate
      >
        {
          hits && hits.map((item, i) => (
            <Slide
              taxonProducts
              key={item.rank}
              product={item}
              index={i + 1}
              showQuickShop={false}
              module="taxon_products"
              onClick={(e) => {
                trackProductClick({ item });
                const isWishlist = e.target.dataset.id === 'wishlist';
                if (isWishlist) {
                  e.preventDefault();
                  return false;
                }
                return true;
              }}
            />
          ))
        }
      </Carousel>
    );
  }

  return (
    <NoSlider numChildren={hits.length}>
      {
        hits && hits.map((item, i) => (
          <Product
            taxonProducts
            key={item.rank}
            product={item}
            index={i + 1}
            showQuickShop={false}
            module="taxon_products"
            className={setStyle(props.style, i)}
            onClick={(e) => {
              trackProductClick({ item });
              const isWishlist = e.target.dataset.id === 'wishlist';
              if (isWishlist) {
                e.preventDefault();
                return false;
              }
              return true;
            }}
          />
        ))
      }
    </NoSlider>
  );
};

TaxonProductWrapper.defaultProps = {
  isSlider: false,
  style: ''
};

TaxonProductWrapper.propTypes = {
  hits: PropTypes.object.isRequired,
  isSlider: PropTypes.bool,
  style: PropTypes.string
};

const HitsComponent = connectHits(TaxonProductWrapper);

const TaxonProductsAlgolia = (props) => (
  <InstantSearch
    searchClient={props.searchClient}
    searchState={props.searchState}
    createURL={props.createURL}
    indexName={props.indexName}
    {...props}
  >
    {createRefinementAttributes(props.searchState)}
    <Configure hitsPerPage={props.productLimit} />
    <HitsComponent {...props} />
  </InstantSearch>
);

TaxonProductsAlgolia.defaultProps = {
  brand: null,
  edit: null,
  children: null,
  miniFilters: {},
  lpc: null,
  trend: null,
  productLimit: 4
};

TaxonProductsAlgolia.propTypes = {
  brand: PropTypes.object,
  children: PropTypes.node,
  createURL: PropTypes.func.isRequired,
  edit: PropTypes.string,
  miniFilters: PropTypes.object,
  indexName: PropTypes.string.isRequired,
  lpc: PropTypes.string,
  onSearchStateChange: PropTypes.func.isRequired,
  resultsState: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  searchClient: PropTypes.object.isRequired,
  searchState: PropTypes.object.isRequired,
  trend: PropTypes.string,
  productLimit: PropTypes.number
};

export default TaxonProductsAlgolia;
